import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { DrillDetail } from '@/components/training/DrillDetail';
import { EmptyState } from '@/components/training/EmptyStates';
import { getLatestTrainingPlan } from '@/lib/api/training-program';
import { loadTrainingProgram, type LoadedTrainingProgram } from '@/lib/training-program/program-loader';
import type { ProgramDrill } from '@/types/training-program';
import type { TrainingPlan } from '@/types/curriculum';

export default function DrillDetailPage() {
  const { drillId } = useParams<{ drillId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState<LoadedTrainingProgram | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const response = await getLatestTrainingPlan();
      const planJson = response?.plan?.plan_json as TrainingPlan | undefined;
      if (!planJson) {
        if (!cancelled) {
          setProgramData(null);
          setLoading(false);
        }
        return;
      }
      const loaded = await loadTrainingProgram(planJson);
      if (!cancelled) {
        setProgramData(loaded);
        setLoading(false);
      }
    };
    load().catch(() => {
      if (!cancelled) {
        setProgramData(null);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const drill = useMemo<ProgramDrill | null>(() => {
    if (!programData || !drillId) return null;
    return (
      programData.program.required_drills.find((item) => item.drill_id === drillId) ||
      programData.program.optional_drills.find((item) => item.drill_id === drillId) ||
      null
    );
  }, [programData, drillId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!programData || !drillId || !drill) {
    return <EmptyState type="unavailable" />;
  }

  return (
    <DrillDetail
      drill={drill}
      program={programData.program}
      onBack={() => navigate(-1)}
      onStart={(id) => navigate(`/training-program/start/${id}`)}
    />
  );
}
